<div class="terms-of-service-container bg-gray-50 min-h-screen">
  <!-- Hero Section -->
  <section class="bg-gradient-to-r from-immovea-navy via-blue-800 to-blue-900 text-white py-12 sm:py-20 lg:py-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl">
        <h1 class="text-4xl sm:text-5xl font-bold mb-3 sm:mb-4">Terms of Service</h1>
        <p class="text-base sm:text-xl text-blue-100 leading-relaxed">Please read these terms carefully before using our services.</p>
        <p class="text-sm text-blue-200 mt-4">Last Updated: {{ lastUpdated }}</p>
      </div>
    </div>
  </section>

  <!-- Content Section -->
  <div class="max-w-4xl mx-auto py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
    <div class="bg-white rounded-xl shadow-lg p-6 sm:p-10 space-y-8">
      
      <!-- Introduction -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">1. Agreement to Terms</h2>
        <p class="text-gray-700 leading-relaxed">
          Welcome to IMMOVÉA. These Terms of Service ("Terms") govern your access to and use of our website, services, and applications (collectively, the "Services"). By accessing or using our Services, you agree to be bound by these Terms and our Privacy Policy.
        </p>
        <p class="text-gray-700 leading-relaxed mt-3">
          If you do not agree to these Terms, you may not access or use our Services. These Terms constitute a legally binding agreement between you and IMMOVÉA Real Estate, operating under Lebanese law.
        </p>
      </section>

      <!-- Definitions -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">2. Definitions</h2>
        <ul class="space-y-3 text-gray-700">
          <li><strong>"Company," "we," "us," or "our"</strong> refers to IMMOVÉA Real Estate</li>
          <li><strong>"Services"</strong> refers to our website, property listings, consultation services, and related offerings</li>
          <li><strong>"User," "you," or "your"</strong> refers to any individual or entity accessing our Services</li>
          <li><strong>"Content"</strong> refers to all text, images, videos, property listings, and other materials on our platform</li>
          <li><strong>"Property"</strong> refers to real estate listings displayed on our platform</li>
        </ul>
      </section>

      <!-- Eligibility -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">3. Eligibility</h2>
        <p class="text-gray-700 leading-relaxed">
          You must be at least 18 years of age to use our Services. By using our Services, you represent and warrant that you meet this age requirement and have the legal capacity to enter into these Terms under Lebanese law.
        </p>
      </section>

      <!-- Use of Services -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">4. Use of Services</h2>
        
        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">4.1 License to Use</h3>
        <p class="text-gray-700 leading-relaxed">
          Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use our Services for personal, non-commercial purposes.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">4.2 Prohibited Uses</h3>
        <p class="text-gray-700 leading-relaxed mb-3">You agree not to:</p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4">
          <li>Use the Services for any illegal purpose or in violation of Lebanese laws</li>
          <li>Impersonate any person or entity or misrepresent your affiliation</li>
          <li>Transmit any viruses, malware, or other harmful code</li>
          <li>Interfere with or disrupt the Services or servers</li>
          <li>Scrape, copy, or download property listings for commercial purposes without authorization</li>
          <li>Use automated systems (bots, scrapers) to access the Services</li>
          <li>Post false, misleading, or fraudulent information</li>
          <li>Harass, abuse, or harm other users or our staff</li>
          <li>Attempt to gain unauthorized access to our systems</li>
          <li>Remove or modify any copyright, trademark, or proprietary notices</li>
        </ul>
      </section>

      <!-- Property Listings -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">5. Property Listings and Information</h2>
        
        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">5.1 Accuracy of Information</h3>
        <p class="text-gray-700 leading-relaxed">
          While we strive to provide accurate and up-to-date property information, we do not guarantee the accuracy, completeness, or reliability of any listings. Property details, prices, availability, and features are subject to change without notice.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.2 Third-Party Listings</h3>
        <p class="text-gray-700 leading-relaxed">
          Some property listings may be provided by third parties. We are not responsible for the accuracy of third-party information or the condition of properties listed by others. All transactions are between you and the property owner or their authorized representative.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">5.3 No Guarantee of Availability</h3>
        <p class="text-gray-700 leading-relaxed">
          Property listings do not constitute an offer to sell. Properties may be sold, rented, or removed from the market at any time without notice.
        </p>
      </section>

      <!-- User Submissions -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">6. User Submissions and Communications</h2>
        <p class="text-gray-700 leading-relaxed">
          When you submit information through our contact forms, property valuation requests, or other communication channels, you grant us the right to use, store, and process this information to provide our Services and respond to your inquiries.
        </p>
        <p class="text-gray-700 leading-relaxed mt-3">
          You represent and warrant that any information you provide is accurate, complete, and does not violate any third-party rights or applicable laws.
        </p>
      </section>

      <!-- Intellectual Property -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property Rights</h2>
        
        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">7.1 Our Property</h3>
        <p class="text-gray-700 leading-relaxed">
          All content, features, and functionality of our Services, including but not limited to text, graphics, logos, images, designs, software, and compilation thereof, are the exclusive property of IMMOVÉA or its licensors and are protected by Lebanese and international copyright, trademark, and other intellectual property laws.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">7.2 Trademarks</h3>
        <p class="text-gray-700 leading-relaxed">
          "IMMOVÉA" and related logos are trademarks or registered trademarks of IMMOVÉA Real Estate. You may not use these marks without our prior written permission.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">7.3 Limited Use Rights</h3>
        <p class="text-gray-700 leading-relaxed">
          You may view, download, and print content from our Services solely for your personal, non-commercial use, provided you do not modify the content and retain all copyright and proprietary notices.
        </p>
      </section>

      <!-- Disclaimer of Warranties -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">8. Disclaimer of Warranties</h2>
        <p class="text-gray-700 leading-relaxed">
          OUR SERVICES ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. TO THE FULLEST EXTENT PERMITTED BY LEBANESE LAW, WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO:
        </p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
          <li>Implied warranties of merchantability and fitness for a particular purpose</li>
          <li>Accuracy, reliability, or completeness of property listings</li>
          <li>Uninterrupted, secure, or error-free operation of the Services</li>
          <li>Results obtained from using the Services</li>
          <li>Quality of properties listed on our platform</li>
        </ul>
        <p class="text-gray-700 leading-relaxed mt-4">
          We do not warrant that our Services will meet your requirements or expectations.
        </p>
      </section>

      <!-- Limitation of Liability -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">9. Limitation of Liability</h2>
        <p class="text-gray-700 leading-relaxed">
          TO THE MAXIMUM EXTENT PERMITTED BY LEBANESE LAW, IMMOVÉA, ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SERVICES, INCLUDING BUT NOT LIMITED TO:
        </p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
          <li>Loss of profits, revenue, or business opportunities</li>
          <li>Loss of data or information</li>
          <li>Property damage or personal injury</li>
          <li>Decisions made based on property information</li>
          <li>Transactions with third parties through our Services</li>
        </ul>
        <p class="text-gray-700 leading-relaxed mt-4">
          Our total liability for any claims related to the Services shall not exceed the amount you paid us, if any, in the twelve (12) months prior to the claim.
        </p>
      </section>

      <!-- Indemnification -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">10. Indemnification</h2>
        <p class="text-gray-700 leading-relaxed">
          You agree to indemnify, defend, and hold harmless IMMOVÉA, its affiliates, officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, costs, or expenses (including reasonable attorneys' fees) arising out of or related to:
        </p>
        <ul class="list-disc list-inside text-gray-700 space-y-2 ml-4 mt-3">
          <li>Your use of the Services</li>
          <li>Your violation of these Terms</li>
          <li>Your violation of any rights of third parties</li>
          <li>Any information or content you submit through the Services</li>
        </ul>
      </section>

      <!-- Professional Services -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">11. Professional Real Estate Services</h2>
        <p class="text-gray-700 leading-relaxed">
          IMMOVÉA provides real estate brokerage and consultation services in compliance with Lebanese real estate regulations. However, we are not providing legal, tax, financial, or investment advice. You should consult with appropriate professionals before making any real estate decisions.
        </p>
        <p class="text-gray-700 leading-relaxed mt-3">
          Any agreements for property purchase, sale, or rental are between you and the property owner or their authorized representative. IMMOVÉA acts as an intermediary and is not a party to such transactions.
        </p>
      </section>

      <!-- Third-Party Services -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">12. Third-Party Services and Links</h2>
        <p class="text-gray-700 leading-relaxed">
          Our Services may contain links to third-party websites or services. We are not responsible for the content, privacy policies, or practices of third-party sites. Your use of third-party services is at your own risk and subject to their terms and conditions.
        </p>
      </section>

      <!-- Termination -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">13. Termination</h2>
        <p class="text-gray-700 leading-relaxed">
          We reserve the right to suspend or terminate your access to our Services at any time, without notice, for any reason, including but not limited to violation of these Terms. Upon termination, your right to use the Services will immediately cease.
        </p>
        <p class="text-gray-700 leading-relaxed mt-3">
          Sections that by their nature should survive termination will remain in effect, including but not limited to intellectual property provisions, disclaimers, indemnification, and limitations of liability.
        </p>
      </section>

      <!-- Dispute Resolution -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">14. Dispute Resolution and Governing Law</h2>
        
        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-4">14.1 Governing Law</h3>
        <p class="text-gray-700 leading-relaxed">
          These Terms shall be governed by and construed in accordance with the laws of the Lebanese Republic, without regard to its conflict of law provisions.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">14.2 Jurisdiction</h3>
        <p class="text-gray-700 leading-relaxed">
          Any disputes arising out of or relating to these Terms or the Services shall be subject to the exclusive jurisdiction of the courts of Lebanon.
        </p>

        <h3 class="text-xl font-semibold text-gray-800 mb-3 mt-6">14.3 Informal Resolution</h3>
        <p class="text-gray-700 leading-relaxed">
          Before filing any legal claim, you agree to first contact us and attempt to resolve the dispute informally. We will make reasonable efforts to resolve disputes amicably.
        </p>
      </section>

      <!-- Changes to Terms -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">15. Changes to Terms</h2>
        <p class="text-gray-700 leading-relaxed">
          We reserve the right to modify these Terms at any time. We will notify users of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use of the Services after such changes constitutes acceptance of the modified Terms.
        </p>
        <p class="text-gray-700 leading-relaxed mt-3">
          We recommend reviewing these Terms periodically for any updates.
        </p>
      </section>

      <!-- Severability -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">16. Severability</h2>
        <p class="text-gray-700 leading-relaxed">
          If any provision of these Terms is held to be invalid, illegal, or unenforceable under Lebanese law, the remaining provisions shall continue in full force and effect.
        </p>
      </section>

      <!-- Entire Agreement -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">17. Entire Agreement</h2>
        <p class="text-gray-700 leading-relaxed">
          These Terms, together with our Privacy Policy, constitute the entire agreement between you and IMMOVÉA regarding the use of our Services and supersede all prior agreements and understandings.
        </p>
      </section>

      <!-- Waiver -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">18. Waiver</h2>
        <p class="text-gray-700 leading-relaxed">
          Our failure to enforce any right or provision of these Terms shall not constitute a waiver of such right or provision. Any waiver must be in writing and signed by an authorized representative of IMMOVÉA.
        </p>
      </section>

      <!-- Contact Information -->
      <section>
        <h2 class="text-2xl font-bold text-gray-900 mb-4">19. Contact Information</h2>
        <p class="text-gray-700 leading-relaxed mb-4">
          If you have any questions about these Terms of Service, please contact us:
        </p>
        <div class="bg-blue-50 border-l-4 border-immovea-navy p-6 rounded-r-lg">
          <p class="text-gray-800 font-semibold mb-2">IMMOVÉA Real Estate</p>
          <p class="text-gray-700">Email: immovea.realestate&#64;gmail.com</p>
          <p class="text-gray-700">Website: immovea.realestate</p>
          <p class="text-gray-700 mt-2">Lebanon</p>
        </div>
      </section>

      <!-- Acknowledgment -->
      <section class="border-t-2 border-gray-200 pt-6">
        <h2 class="text-2xl font-bold text-gray-900 mb-4">20. Acknowledgment</h2>
        <p class="text-gray-700 leading-relaxed">
          BY USING OUR SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF SERVICE AND OUR PRIVACY POLICY.
        </p>
      </section>

    </div>
  </div>
</div>
